import { Analysis, Granularity, Metric } from '../dashboard-config.model';
import { GRANULARITY_PROFILES, GranularityTrait } from '../granularities/granularity-profile';
import { KpiOption } from './kpi-options';

/** A derived figure of a KPI; each maps to one dataset column. */
export type KpiMeasure =
  | 'value'
  | 'prevYear'
  | 'changePrevYearPct'
  | 'changePrevQuarterPct'
  | 'cagr'
  | 'rank'
  | 'ciHigh'
  | 'ciLow'
  | 'lq'
  | 'lqChangePrevYearPct'
  /** Share of the parent scope: Germany, the state, or the Σ sub-market. */
  | 'shareOfCountryPct'
  | 'shareOfStatePct'
  | 'shareOfSumSubMarketPct'
  /** District-level value and its share of the district population (employed ranking). */
  | 'districtValue'
  | 'districtPopulationPct'
  /** State-level value of the row (employed index). */
  | 'stateValue'
  /** Σ sub-market value of the row and its share of Germany (WZ-Klassen ranking). */
  | 'subMarketValue'
  | 'subMarketShareOfCountryPct'
  /** State share of Germany (dossier info). */
  | 'stateShareOfCountryPct'
  /** Largest Teilmarkt of the state, its id and value (dossier info). */
  | 'topSubMarketId'
  | 'topSubMarketValue'
  /** Sankey link source / target values and target share of source (dossier overview, onion). */
  | 'sankeySourceValue'
  | 'sankeyTargetValue'
  | 'sankeyTargetShareOfSourcePct';

/** Where the column is read; only needed for dataset quirks. */
export interface KpiColumnContext {
  analysis?: Analysis;
  granularity?: Granularity;
}

/** Dataset naming: KPI id (column stem) + measure suffix; k€ KPIs keep `__k_eur` on value columns. */
function defaultKpiColumn(kpi: KpiOption, measure: KpiMeasure): string {
  const kEur = kpi.unit === 'kEur' ? '__k_eur' : '';
  switch (measure) {
    case 'value': return kpi.valueColumn;
    case 'prevYear': return `${kpi.value}_prev_year${kEur}`;
    case 'changePrevYearPct': return `${kpi.value}_change_prev_year_pct`;
    case 'changePrevQuarterPct': return `${kpi.value}_change_prev_quarter_pct`;
    case 'cagr': return `${kpi.value}_cagr`;
    case 'rank': return `${kpi.value}_rank`;
    case 'ciHigh': return `${kpi.valueColumn}_confidence_interval_high`;
    case 'ciLow': return `${kpi.valueColumn}_confidence_interval_low`;
    case 'lq': return `${kpi.value}_lq`;
    case 'lqChangePrevYearPct': return `${kpi.value}_lq_change_prev_year_pct`;
    case 'shareOfCountryPct': return `${kpi.value}_country_pct`;
    case 'shareOfStatePct': return `${kpi.value}_state_pct`;
    case 'shareOfSumSubMarketPct': return `${kpi.value}_sum_sub_market_pct`;
    case 'districtValue': return `${kpi.value}_district${kEur}`;
    case 'stateValue': return `${kpi.value}_state${kEur}`;
    case 'districtPopulationPct': return `${kpi.value}_district_population_pct`;
    case 'subMarketValue': return `${kpi.value}_sub_market${kEur}`;
    case 'subMarketShareOfCountryPct': return `${kpi.value}_sub_market_country_pct`;
    case 'stateShareOfCountryPct': return `${kpi.value}_state_country_pct`;
    case 'topSubMarketId': return `${kpi.value}_top_sub_market_id`;
    case 'topSubMarketValue': return `${kpi.value}_top_sub_market`;
    case 'sankeySourceValue': return `source_${kpi.valueColumn}`;
    case 'sankeyTargetValue': return `target_${kpi.valueColumn}`;
    case 'sankeyTargetShareOfSourcePct': return `${kpi.value}_target_source_pct`;
  }
}

/** A dataset that deviates from the default naming. Each entry says why. */
interface KpiColumnOverride {
  reason: string;
  metrics: readonly Metric[];
  measure: KpiMeasure;
  analysis?: Analysis;
  /** Applies only at granularities with this trait. */
  trait?: GranularityTrait;
  /** Applies only at these granularities. */
  granularities?: readonly Granularity[];
  column: (kpi: KpiOption) => string;
}

/**
 * Known dataset quirks (PROFILE-REMODEL-FINDINGS.md §4.2). Only quirks of
 * migrated measures live here; add the rest as their families migrate.
 */
const KPI_COLUMN_OVERRIDES: readonly KpiColumnOverride[] = [
  {
    reason: 'Time-series WZ revenue datasets keep __k_eur before the change suffix.',
    metrics: [Metric.CompaniesRevenue],
    measure: 'changePrevYearPct',
    analysis: Analysis.TimeSeries,
    trait: 'wz',
    column: (kpi) => `${kpi.valueColumn}_change_prev_year_pct`,
  },
  {
    reason: 'Time-series GVA datasets keep __k_eur before the change suffix.',
    metrics: [Metric.GrossValueAdded],
    measure: 'changePrevYearPct',
    analysis: Analysis.TimeSeries,
    column: (kpi) => `${kpi.valueColumn}_change_prev_year_pct`,
  },
  {
    reason: 'State-level ranking datasets of economic participants and employed name the Germany share _country_country_pct.',
    metrics: [Metric.EconomicParticipant, Metric.Employed],
    measure: 'shareOfCountryPct',
    analysis: Analysis.Ranking,
    granularities: [Granularity.States],
    column: (kpi) => `${kpi.value}_country_country_pct`,
  },
  {
    reason: 'Employed ranking datasets misspell the column (popoulation); keep until the backend renames it.',
    metrics: [Metric.Employed],
    measure: 'districtPopulationPct',
    analysis: Analysis.Ranking,
    column: (kpi) => `${kpi.value}_district_popoulation_pct`,
  },
  {
    reason: 'The comparison district dataset (ca__d) carries Teilmarkt×Kreis employed values under *_sub_market_district.',
    metrics: [Metric.Employed],
    measure: 'value',
    analysis: Analysis.Comparison,
    granularities: [Granularity.DistrictSubMarkets],
    column: (kpi) => `${kpi.value}_sub_market_district`,
  },
  {
    reason: 'Forecast revenue datasets keep __k_eur before the change suffix.',
    metrics: [Metric.CompaniesRevenue],
    measure: 'changePrevYearPct',
    analysis: Analysis.Forecast,
    column: (kpi) => `${kpi.valueColumn}_change_prev_year_pct`,
  },
  ...(['ciHigh', 'ciLow'] as const).map((measure): KpiColumnOverride => ({
    reason: 'Forecast companies-count datasets name the confidence interval companies_* / small_companies_*.',
    metrics: [Metric.CompaniesCount],
    measure,
    analysis: Analysis.Forecast,
    column: (kpi) => `${kpi.value.replace(/_count$/, '')}_confidence_interval_${measure === 'ciHigh' ? 'high' : 'low'}`,
  })),
];

function overrideApplies(
  override: KpiColumnOverride,
  kpi: KpiOption,
  measure: KpiMeasure,
  context: KpiColumnContext,
): boolean {
  if (!override.metrics.includes(kpi.metricId) || override.measure !== measure) return false;
  if (override.analysis !== undefined && override.analysis !== context.analysis) return false;
  if (override.granularities && (context.granularity === undefined || !override.granularities.includes(context.granularity))) {
    return false;
  }
  if (override.trait !== undefined) {
    const traits = context.granularity === undefined ? [] : GRANULARITY_PROFILES[context.granularity]?.traits ?? [];
    if (!traits.includes(override.trait)) return false;
  }
  return true;
}

/** Dataset column for a KPI measure, honouring known quirks for the context. */
export function kpiColumn(kpi: KpiOption, measure: KpiMeasure, context: KpiColumnContext = {}): string {
  const override = KPI_COLUMN_OVERRIDES.find((candidate) => overrideApplies(candidate, kpi, measure, context));
  return override ? override.column(kpi) : defaultKpiColumn(kpi, measure);
}
